import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { UnitSystem } from "@/lib/units";

/**
 * App-wide display settings, persisted per browser in localStorage.
 *
 *   uiUnits   units the Calculator / Gantt screens display and accept
 *   pdfUnits  units the exported PDFs are rendered in
 *
 * Kept separate so you can work in SI and still hand a customer a US PDF.
 * Neither touches calculation inputs, saved projects or API payloads.
 */
export interface Settings {
  uiUnits: UnitSystem;
  pdfUnits: UnitSystem;
}

export const SETTINGS_KEY = "jackstone-vpf-settings-v1";
const DEFAULTS: Settings = { uiUnits: "SI", pdfUnits: "SI" };

const isSystem = (v: unknown): v is UnitSystem => v === "SI" || v === "US";

/** Read settings outside React (PDF payload builders). Defaults on any error. */
export function loadSettings(): Settings {
  try {
    const raw = JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}");
    return {
      uiUnits: isSystem(raw?.uiUnits) ? raw.uiUnits : DEFAULTS.uiUnits,
      pdfUnits: isSystem(raw?.pdfUnits) ? raw.pdfUnits : DEFAULTS.pdfUnits,
    };
  } catch {
    return DEFAULTS;
  }
}

interface SettingsContextValue extends Settings {
  update: (patch: Partial<Settings>) => void;
}

const SettingsContext = createContext<SettingsContextValue>({ ...DEFAULTS, update: () => {} });

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  useEffect(() => {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); }
    catch { /* storage unavailable — settings last for this tab only */ }
  }, [settings]);

  // Another tab changed the settings — follow it
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === SETTINGS_KEY) setSettings(loadSettings()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const update = useCallback((patch: Partial<Settings>) => setSettings((s) => ({ ...s, ...patch })), []);
  const value = useMemo(() => ({ ...settings, update }), [settings, update]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export const useSettings = () => useContext(SettingsContext);
